import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Form, Modal } from 'react-bootstrap'
import ReactReadMoreReadLess from 'react-read-more-read-less'
import { FaArrowDown, FaArrowRight, FaArrowUp, FaExchangeAlt, FaShieldAlt, FaTimesCircle } from 'react-icons/fa'
import { FaEye } from 'react-icons/fa6'
import Lightbox from 'react-image-lightbox'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { KTSVG } from '../../../_metronic/helpers'
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import DateComp from '../../../components/dateComponent/DateComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp'
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu'
import TextField from '../../../components/textFields/TextField'
import { constraintConfig } from '../../../constraintConfig'
import useColumnVisibility from '../../customHooks/useColumnVisibility'
import { useGetBankLineItemsForMergeV2Query } from '../../../services/AutomationApi'
import { useGetSSLineItemsForMergeV2Query } from '../../../services/ExpenseApi'
import labelKey from '../../localization/label.json'
import MergeLineItemConfirmationModalV2, { MergeV2Mode } from './MergeLineItemConfirmationModalV2'
import { resolveMergePair } from './mergeFinalPreview'
import MergeResultPreviewRow from './MergeResultPreviewRow'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../features/authSlice'
import MergeSelectedLineItemHeader, {
  canViewInvoiceDataDraftId,
  formatReceiptNumberDisplay,
  getMergeAdminMeta,
  getMergeAttachmentUrl,
  getReceiptNumber,
  isMergeAdminViewer,
} from './MergeSelectedLineItemHeader'

const MERGE_V2_COLUMNS_PAGE = 'mergeLineItemsV2'
/** Load all merge candidates in one request (no list pagination). */
const MERGE_V2_ALL_PAGE_SIZE = 5000

// #region MergeLineItems V2
// Better Line Item Merge (user-facing):
// Case 1 — SS (Web/Mobile) → Bank (amount-prefer + same-amount filter)
// Case 2 — SS → SS (Into keep / From soft-delete + reorder for same amount)
// Enabled when OrgSetting IsLineItemMergeV2Enabled = true (see MergeExpenseGate).
// #endregion MergeLineItems V2

type Props = {
  show: boolean
  handleClose: () => void
  data?: any
  selectedDraftData: any
  refetch?: any
  setShowInnerTable?: any
  onSuccess?: () => void
}

type MergeTab = 'all' | 'bank' | 'ss'
type MatchFilterMode = 'amount' | 'date' | 'dateAndAmount' | 'all'
type MergeCandidate = any & { _mergeSource?: 'bank' | 'ss'; _isOpeningItem?: boolean }

const parseAmount = (value: any): number => {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const candidateMatchesListSearch = (item: MergeCandidate, query: string): boolean => {
  if (!query) return true
  const haystack = [
    item?.trackingId,
    item?.expenseItemId,
    item?.invoiceDataDraftId,
    item?.amount,
    item?.itemDetail,
    item?.title,
    item?.expenseCategory?.title,
    item?.vendor?.name,
    item?.paymentMethod?.title,
    item?.expenseStatusTitle,
    item?.expenseStatus?.title,
    getMergeAdminMeta(item).reportStatus,
    getReceiptNumber(item),
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ')
  return haystack.includes(query)
}

const sameItemDate = (a: any, b: any): boolean => {
  if (!a || !b) return false
  return String(a).split('T')[0] === String(b).split('T')[0]
}

/** Higher = better. 3 = date+amount, 2 = amount, 1 = date, 0 = none. */
const getMatchScore = (item: any, preferAmount: number, preferItemDate: any): number => {
  const amountOk = preferAmount > 0 && Math.abs(parseAmount(item?.amount) - preferAmount) < 0.001
  const dateOk = sameItemDate(item?.itemDate, preferItemDate)
  if (amountOk && dateOk) return 3
  if (amountOk) return 2
  if (dateOk) return 1
  return 0
}

const getFieldMatchFlags = (item: any, preferAmount: number, preferItemDate: any) => {
  const amountMatch = preferAmount > 0 && Math.abs(parseAmount(item?.amount) - preferAmount) < 0.001
  const dateMatch = sameItemDate(item?.itemDate, preferItemDate)
  return { amountMatch, dateMatch }
}

const BetterLineItemMergeModal = ({ show, handleClose, selectedDraftData, refetch, onSuccess }: Props) => {
  const { mode: themeMode } = useThemeMode()
  const isMergeDark =
    themeMode === 'dark' ||
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-bs-theme') === 'dark')
  const { roleID, userAccountID } = useSelector(selectAuth)
  const showAdminMeta = isMergeAdminViewer(roleID)
  const showInvoiceDataDraftId = canViewInvoiceDataDraftId(roleID)
  const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: MERGE_V2_COLUMNS_PAGE })

  // One-time: Title column hidden by default (still available via Columns menu)
  useEffect(() => {
    if (!userAccountID) return
    const flag = `mergeLineItemsV2_hideTitle_migrated_${userAccountID}`
    if (localStorage.getItem(flag)) return
    localStorage.setItem(flag, '1')
    if (hiddenColumns.includes('title')) {
      handleToggleColumn('title')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccountID])
  const [activeTab, setActiveTab] = useState<MergeTab>('all')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [listSearch, setListSearch] = useState('')
  const [matchFilter, setMatchFilter] = useState<MatchFilterMode>('all')
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null)
  const [selectedTargetData, setSelectedTargetData] = useState<MergeCandidate | null>(null)
  /** Below compare panel opens only via attachment → arrow, not on best-match auto-select. */
  const [showComparePanel, setShowComparePanel] = useState(false)
  /** SS↔SS only: flip From/Into. Ignored when Bank is involved (Bank always Into). */
  const [mergePairSwapped, setMergePairSwapped] = useState(false)
  const [orderedSsIds, setOrderedSsIds] = useState<number[]>([])
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState('')
  const modalBodyRef = useRef<HTMLDivElement | null>(null)

  const preferAmount = useMemo(() => parseAmount(selectedDraftData?.amount), [selectedDraftData])
  const preferItemDate = selectedDraftData?.itemDate || undefined
  const excludeExpenseItemId = selectedDraftData?.expenseItemId || 0
  const sameAmountOnly = matchFilter === 'amount' || matchFilter === 'dateAndAmount'
  const sameItemDateOnly = matchFilter === 'date' || matchFilter === 'dateAndAmount'

  const isPdfFile = (filePath: string | undefined) => {
    if (!filePath) return false
    return filePath.toLowerCase().includes('.pdf')
  }

  const openPdfInBrowserOrLightbox = (attachmentFile: string | undefined) => {
    if (!attachmentFile) return
    if (isPdfFile(attachmentFile)) {
      window.open(attachmentFile, '_blank')
      return
    }
    setLightboxUrl(attachmentFile)
    setLightboxOpen(true)
  }

  const bankQuery = useGetBankLineItemsForMergeV2Query(
    {
      pageNumber: 1,
      pageSize: MERGE_V2_ALL_PAGE_SIZE,
      preferAmount,
      sameAmountOnly,
      sameItemDateOnly,
      preferItemDate,
    },
    { skip: !show || (activeTab !== 'bank' && activeTab !== 'all') }
  )

  const ssQuery = useGetSSLineItemsForMergeV2Query(
    {
      pageNumber: 1,
      pageSize: MERGE_V2_ALL_PAGE_SIZE,
      preferAmount,
      sameAmountOnly,
      sameItemDateOnly,
      preferItemDate,
      excludeExpenseItemId,
    },
    { skip: !show || (activeTab !== 'ss' && activeTab !== 'all') }
  )

  const bankRows: MergeCandidate[] = useMemo(
    () => (bankQuery.data?.result?.data || []).map((r: any) => ({ ...r, _mergeSource: 'bank' as const })),
    [bankQuery.data]
  )
  const ssRows: MergeCandidate[] = useMemo(
    () => (ssQuery.data?.result?.data || []).map((r: any) => ({ ...r, _mergeSource: 'ss' as const })),
    [ssQuery.data]
  )

  const allCombinedSorted = useMemo(() => {
    const combined = [...bankRows, ...ssRows]
    return [...combined].sort((a, b) => {
      const scoreDiff =
        getMatchScore(b, preferAmount, preferItemDate) - getMatchScore(a, preferAmount, preferItemDate)
      if (scoreDiff !== 0) return scoreDiff
      return (b.expenseItemId || 0) - (a.expenseItemId || 0)
    })
  }, [bankRows, ssRows, preferAmount, preferItemDate])

  const rows: MergeCandidate[] = useMemo(() => {
    if (activeTab === 'all') return allCombinedSorted
    if (activeTab === 'bank') return bankRows
    return ssRows
  }, [activeTab, bankRows, ssRows, allCombinedSorted])

  const isLoading =
    activeTab === 'all'
      ? bankQuery.isFetching || ssQuery.isFetching
      : activeTab === 'bank'
        ? bankQuery.isFetching
        : ssQuery.isFetching

  useEffect(() => {
    if (activeTab === 'ss') {
      setOrderedSsIds(ssRows.map((r) => r.expenseItemId))
    }
  }, [activeTab, ssQuery.data])

  const displayRows = useMemo(() => {
    if (activeTab !== 'ss' || !orderedSsIds.length) {
      // Keep best matches first on bank / all (all already sorted)
      if (activeTab === 'bank') {
        return [...rows].sort(
          (a, b) =>
            getMatchScore(b, preferAmount, preferItemDate) - getMatchScore(a, preferAmount, preferItemDate)
        )
      }
      return rows
    }
    const map = new Map(rows.map((r) => [r.expenseItemId, r]))
    const ordered = orderedSsIds.map((id) => map.get(id)).filter(Boolean) as MergeCandidate[]
    const missing = rows.filter((r) => !orderedSsIds.includes(r.expenseItemId))
    const combined = [...ordered, ...missing]
    // Surface best matches at top while preserving relative order within same score
    return [...combined].sort((a, b) => {
      const scoreDiff =
        getMatchScore(b, preferAmount, preferItemDate) - getMatchScore(a, preferAmount, preferItemDate)
      if (scoreDiff !== 0) return scoreDiff
      return orderedSsIds.indexOf(a.expenseItemId) - orderedSsIds.indexOf(b.expenseItemId)
    })
  }, [activeTab, rows, orderedSsIds, preferAmount, preferItemDate])

  const bestMatchScore = useMemo(() => {
    let max = 0
    for (const r of displayRows) {
      max = Math.max(max, getMatchScore(r, preferAmount, preferItemDate))
    }
    return max
  }, [displayRows, preferAmount, preferItemDate])

  // Auto-select best match radio only (do not open the below compare panel)
  useEffect(() => {
    if (!show || isLoading || selectedTargetId != null) return
    if (bestMatchScore <= 0 || displayRows.length === 0) return
    const best = displayRows.find((r) => getMatchScore(r, preferAmount, preferItemDate) === bestMatchScore)
    if (best?.expenseItemId) {
      setSelectedTargetId(best.expenseItemId)
      setSelectedTargetData(best)
      setMergePairSwapped(false)
      setShowComparePanel(false)
    }
  }, [show, isLoading, displayRows, bestMatchScore, preferAmount, preferItemDate, selectedTargetId])

  useEffect(() => {
    if (show) {
      setListSearch('')
      setSelectedTargetId(null)
      setSelectedTargetData(null)
      setMergePairSwapped(false)
      setShowComparePanel(false)
      setActiveTab('all')
      setMatchFilter('all')
    }
  }, [show, selectedDraftData?.expenseItemId])

  const handleModalClose = useCallback(() => {
    setSelectedTargetId(null)
    setSelectedTargetData(null)
    setMergePairSwapped(false)
    setShowComparePanel(false)
    setShowConfirmation(false)
    handleClose()
  }, [handleClose])

  const handleRadioChange = (id: number, item: MergeCandidate) => {
    setSelectedTargetId(id)
    setSelectedTargetData(item)
    setMergePairSwapped(false)
    setShowComparePanel(true)
    window.setTimeout(() => {
      const panel = document.getElementById('merge-v2-target-panel')
      if (panel) panel.scrollTop = 0
    }, 0)
  }

  const openComparePanel = (id: number, item: MergeCandidate) => {
    setSelectedTargetId(id)
    setSelectedTargetData(item)
    setMergePairSwapped(false)
    setShowComparePanel(true)
    window.setTimeout(() => {
      const panel = document.getElementById('merge-v2-target-panel')
      if (panel) panel.scrollTop = 0
    }, 0)
  }

  const closeComparePanel = () => {
    setSelectedTargetId(null)
    setSelectedTargetData(null)
    setMergePairSwapped(false)
    setShowComparePanel(false)
  }

  const moveSsRow = (expenseItemId: number, direction: 'up' | 'down') => {
    setOrderedSsIds((prev) => {
      const ids = prev.length ? [...prev] : ssRows.map((r) => r.expenseItemId)
      const idx = ids.indexOf(expenseItemId)
      if (idx < 0) return ids
      const swapWith = direction === 'up' ? idx - 1 : idx + 1
      if (swapWith < 0 || swapWith >= ids.length) return ids
        ;[ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]]
      return ids
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedTargetId == null) return
    setShowConfirmation(true)
  }

  const mergePair = useMemo(
    () => resolveMergePair(selectedDraftData, selectedTargetData, mergePairSwapped),
    [selectedDraftData, selectedTargetData, mergePairSwapped]
  )
  const { fromItem, intoItem, canSwap: canSwapMergePair } = mergePair
  const mergeMode: MergeV2Mode = mergePair.mergeMode

  const showOrderColumn = activeTab === 'ss'

  const selectedMatchHints = useMemo(() => {
    if (!fromItem || !intoItem) return undefined
    const preferAmt = parseAmount(fromItem?.amount)
    const preferDate = fromItem?.itemDate
    const flags = getFieldMatchFlags(intoItem, preferAmt, preferDate)
    const score = getMatchScore(intoItem, preferAmt, preferDate)
    return {
      ...flags,
      isBestMatch: bestMatchScore > 0 && score === bestMatchScore,
    }
  }, [fromItem, intoItem, bestMatchScore])

  const switchTab = (tab: MergeTab) => {
    setActiveTab(tab)
    setSelectedTargetId(null)
    setSelectedTargetData(null)
    setMergePairSwapped(false)
    setShowComparePanel(false)
  }

  const handleSwapMergePair = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (!canSwapMergePair || !fromItem || !intoItem) return
    setMergePairSwapped((prev) => !prev)
  }

  const clearTargetSelection = () => {
    setSelectedTargetId(null)
    setSelectedTargetData(null)
    setMergePairSwapped(false)
    setShowComparePanel(false)
  }

  /** Opening item pinned in list when missing from candidates, so Merge From is always visible. */
  const listRows: MergeCandidate[] = useMemo(() => {
    if (!selectedDraftData?.expenseItemId) return displayRows
    const openingId = selectedDraftData.expenseItemId
    if (displayRows.some((r) => Number(r.expenseItemId) === Number(openingId))) return displayRows
    const openingRow: MergeCandidate = {
      ...selectedDraftData,
      _mergeSource:
        selectedDraftData.recordSourceId === constraintConfig.recordSourceId.BankFeed ? 'bank' : 'ss',
      _isOpeningItem: true,
    }
    return [openingRow, ...displayRows]
  }, [displayRows, selectedDraftData])

  const filteredListRows: MergeCandidate[] = useMemo(() => {
    const q = listSearch.trim().toLowerCase()
    if (!q) return listRows
    return listRows.filter((item) => candidateMatchesListSearch(item, q))
  }, [listRows, listSearch])

  const getListMergeRole = (expenseItemId?: number): 'from' | 'to' | null => {
    const id = Number(expenseItemId || 0)
    if (!id) return null
    if (Number(fromItem?.expenseItemId) === id) return 'from'
    if (Number(intoItem?.expenseItemId) === id) return 'to'
    return null
  }

  const renderSourceIcon = (recordSourceId: number) => {
    if (recordSourceId === constraintConfig.recordSourceId.WebApp) {
      return <KTSVG path="/media/icons/figmaIcons/Web.svg" className="svg-icon-1 ms-4 ss-source-icon" />
    }
    if (recordSourceId === constraintConfig.recordSourceId.Mobile) {
      return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className="svg-icon-1 ms-4 ss-source-icon" />
    }
    if (recordSourceId === constraintConfig.recordSourceId.BankFeed) {
      return <KTSVG path="/media/icons/figmaIcons/Bank.svg" className="svg-icon-1 ms-4 ss-source-icon" />
    }
    return null
  }

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        backdrop="static"
        fullscreen
        dialogClassName="modal-dialog modal-dialog-centered"
        contentClassName={`merge-line-items-v2-content${isMergeDark ? ' merge-theme-dark' : ''}`}
      >
        <style>{`
          .merge-line-items-v2-content {
            height: 100vh !important;
            max-height: 100dvh !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          .merge-line-items-v2-content .modal-header {
            flex: 0 0 auto !important;
            padding: 0.45rem 1.25rem !important;
            min-height: 0 !important;
            border-bottom: 1px solid #eff2f5 !important;
          }
          .merge-line-items-v2-content .modal-header h2 {
            margin: 0;
            font-size: 1.15rem;
            line-height: 1.3;
          }
          .merge-line-items-v2-content .modal-body {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
            position: relative;
            padding: 0.5rem 1.25rem 0.25rem 1.25rem !important;
            scrollbar-width: none !important;
          }
          .merge-line-items-v2-content .modal-body::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .merge-line-items-v2-content form#mergeLineItemFormV2 {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          .merge-line-items-v2-content .table-responsive,
          .merge-line-items-v2-content .table-responsive.table-height {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            max-height: 100% !important;
            height: 100% !important;
            overflow-y: auto !important;
            overflow-x: auto !important;
            overscroll-behavior: contain;
            scrollbar-width: thin;
            scrollbar-color: #b5b5c3 #f1f1f4;
          }
          .merge-line-items-v2-content .table-responsive::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .merge-line-items-v2-content .table-responsive::-webkit-scrollbar-track {
            background: #f1f1f4;
            border-radius: 4px;
          }
          .merge-line-items-v2-content .table-responsive::-webkit-scrollbar-thumb {
            background: #b5b5c3;
            border-radius: 4px;
          }
          .merge-line-items-v2-content .table-responsive::-webkit-scrollbar-thumb:hover {
            background: #7e8299;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .table-responsive,
          .merge-theme-dark .table-responsive {
            scrollbar-color: #474761 #1b1b29;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .table-responsive::-webkit-scrollbar-track,
          .merge-theme-dark .table-responsive::-webkit-scrollbar-track {
            background: #1b1b29;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .table-responsive::-webkit-scrollbar-thumb,
          .merge-theme-dark .table-responsive::-webkit-scrollbar-thumb {
            background: #474761;
          }
          .merge-line-items-v2-content .merge-v2-table thead th {
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          .merge-line-items-v2-content #merge-v2-target-panel {
            flex: 0 1 auto !important;
            min-height: 0 !important;
            max-height: 48vh !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            border-top: 1px solid #eff2f5 !important;
            padding: 0.5rem 0.25rem 0.25rem 0.25rem !important;
            margin-top: 0.35rem !important;
            overscroll-behavior: contain !important;
            -webkit-overflow-scrolling: touch !important;
            scrollbar-width: thin !important;
            scrollbar-color: #009ef7 #e1f0ff !important;
          }
          @media (max-height: 800px) {
            .merge-line-items-v2-content #merge-v2-target-panel {
              max-height: 52vh !important;
            }
            .merge-line-items-v2-content form#mergeLineItemFormV2 {
              min-height: 100px !important;
            }
          }
          /* Always visible blue scrollbar for overview section */
          .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar {
            -webkit-appearance: none !important;
            width: 10px !important;
            height: 10px !important;
            display: block !important;
          }
          .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-track {
            -webkit-appearance: none !important;
            background: #e1f0ff !important;
            background-color: #e1f0ff !important;
            border-radius: 6px !important;
          }
          .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-thumb {
            -webkit-appearance: none !important;
            background: #009ef7 !important;
            background-color: #009ef7 !important;
            border-radius: 6px !important;
            border: 2px solid #e1f0ff !important;
          }
          .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-thumb:hover {
            background: #0084cf !important;
            background-color: #0084cf !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content #merge-v2-target-panel,
          .merge-theme-dark #merge-v2-target-panel {
            scrollbar-color: #009ef7 #1b2b44 !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-track,
          .merge-theme-dark #merge-v2-target-panel::-webkit-scrollbar-track {
            background: #1b2b44 !important;
            background-color: #1b2b44 !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-thumb,
          .merge-theme-dark #merge-v2-target-panel::-webkit-scrollbar-thumb {
            background: #009ef7 !important;
            background-color: #009ef7 !important;
            border: 2px solid #1b2b44 !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content #merge-v2-target-panel::-webkit-scrollbar-thumb:hover,
          .merge-theme-dark #merge-v2-target-panel::-webkit-scrollbar-thumb:hover {
            background: #0084cf !important;
            background-color: #0084cf !important;
          }
          .merge-line-items-v2-content .modal-footer {
            flex: 0 0 auto !important;
            padding: 0.4rem 1.25rem !important;
            min-height: 0 !important;
            border-top: 1px solid #eff2f5 !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-header,
          html[data-bs-theme='dark'] .merge-line-items-v2-content #merge-v2-target-panel,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-footer,
          .merge-theme-dark .modal-header,
          .merge-theme-dark #merge-v2-target-panel,
          .merge-theme-dark .modal-footer {
            border-color: #2b2b40 !important;
          }

          /* Checkbox and Radio styling inside merge modal (No blue color) */
          .merge-line-items-v2-content input[type='checkbox'],
          .merge-line-items-v2-content input[type='radio'],
          .merge-line-items-v2-content .form-check-input {
            box-shadow: none !important;
            outline: none !important;
          }
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content input[type='checkbox']:not(:checked),
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content input[type='radio']:not(:checked),
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content .form-check-input:not(:checked) {
            border: 1.5px solid #000000 !important;
            background-color: #ffffff !important;
          }
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content input[type='checkbox']:checked,
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content .form-check-input[type='checkbox']:checked {
            background-color: #181c32 !important;
            border-color: #181c32 !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 13 11' width='13' height='11' fill='none'%3e%3cpath d='M11.0426 1.02893C11.3258 0.695792 11.8254 0.655283 12.1585 0.938451C12.4917 1.22162 12.5322 1.72124 12.249 2.05437L5.51985 9.97104C5.23224 10.3094 4.72261 10.3451 4.3907 10.05L0.828197 6.88335C0.50141 6.59288 0.471975 6.09249 0.762452 5.7657C1.05293 5.43891 1.55332 5.40948 1.88011 5.69995L4.83765 8.32889L11.0426 1.02893Z' fill='%23ffffff'/%3e%3c/svg%3e") !important;
          }
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content input[type='radio']:checked,
          html:not([data-bs-theme='dark']) .merge-line-items-v2-content .form-check-input[type='radio']:checked {
            background-color: #009ef7 !important;
            border-color: #009ef7 !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23ffffff'/%3e%3c/svg%3e") !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content input[type='checkbox']:not(:checked),
          html[data-bs-theme='dark'] .merge-line-items-v2-content input[type='radio']:not(:checked),
          html[data-bs-theme='dark'] .merge-line-items-v2-content .form-check-input:not(:checked),
          .merge-theme-dark input[type='checkbox']:not(:checked),
          .merge-theme-dark input[type='radio']:not(:checked),
          .merge-theme-dark .form-check-input:not(:checked) {
            border: 1.5px solid #ffffff !important;
            background-color: #1e1e2d !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content input[type='checkbox']:checked,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .form-check-input[type='checkbox']:checked,
          .merge-theme-dark input[type='checkbox']:checked,
          .merge-theme-dark .form-check-input[type='checkbox']:checked {
            background-color: #ffffff !important;
            border-color: #ffffff !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 13 11' width='13' height='11' fill='none'%3e%3cpath d='M11.0426 1.02893C11.3258 0.695792 11.8254 0.655283 12.1585 0.938451C12.4917 1.22162 12.5322 1.72124 12.249 2.05437L5.51985 9.97104C5.23224 10.3094 4.72261 10.3451 4.3907 10.05L0.828197 6.88335C0.50141 6.59288 0.471975 6.09249 0.762452 5.7657C1.05293 5.43891 1.55332 5.40948 1.88011 5.69995L4.83765 8.32889L11.0426 1.02893Z' fill='%23181c32'/%3e%3c/svg%3e") !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content input[type='radio']:checked,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .form-check-input[type='radio']:checked,
          .merge-theme-dark input[type='radio']:checked,
          .merge-theme-dark .form-check-input[type='radio']:checked {
            background-color: #009ef7 !important;
            border-color: #009ef7 !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23ffffff'/%3e%3c/svg%3e") !important;
          }
          .merge-v2-best-match-row {
            background-color: #d1f0e1 !important;
            // outline: 2px solid #8fb9a2;
            outline: 2px solid #7fac93;
            outline-offset: -2px;
          }
          .merge-v2-match-row {
            background-color: #e8fff3 !important;
          }
          .merge-v2-from-row {
            background-color: #fff5e8 !important;
            outline: 2px solid #ffc700;
            outline-offset: -2px;
          }
          .merge-v2-to-row {
            background-color: #e8f4ff !important;
            outline: 2px solid #009ef7;
            outline-offset: -2px;
          }
          /* Light mode: panel fills (From yellow / Into blue / Preview green) */
          html:not([data-bs-theme='dark']) .merge-panel--from {
            background-color: #fff8eb !important;
            border-color: #ffc700 !important;
          }
          html:not([data-bs-theme='dark']) .merge-panel--into {
            background-color: #e8f4ff !important;
            border-color: #009ef7 !important;
          }
          html:not([data-bs-theme='dark']) .merge-panel--preview,
          html:not([data-bs-theme='dark']) .merge-result-preview {
            background-color: #e8fff3 !important;
            border-color: #50cd89 !important;
          }
          html:not([data-bs-theme='dark']) .merge-result-preview-header {
            background-color: #d1f0e1 !important;
          }
          html:not([data-bs-theme='dark']) .merge-result-preview-body {
            background-color: #f3fffa !important;
            border-color: #b6e9d0 !important;
          }
          html:not([data-bs-theme='dark']) .merge-panel--from .text-gray-900,
          html:not([data-bs-theme='dark']) .merge-panel--into .text-gray-900,
          html:not([data-bs-theme='dark']) .merge-result-preview .text-gray-900 {
            color: #181c32 !important;
          }
          /* Fluid readable type on merge panels / preview */
          .merge-panel,
          .merge-result-preview {
            --merge-title-size: clamp(0.8rem, 0.72rem + 0.35vw, 0.95rem);
            --merge-label-size: clamp(0.65rem, 0.58rem + 0.25vw, 0.75rem);
            --merge-value-size: clamp(0.72rem, 0.65rem + 0.3vw, 0.875rem);
            --merge-meta-size: clamp(0.62rem, 0.55rem + 0.22vw, 0.72rem);
            --merge-badge-size: clamp(0.58rem, 0.52rem + 0.2vw, 0.68rem);
          }
          .merge-panel h5,
          .merge-result-preview-header .fw-bold {
            font-size: var(--merge-title-size) !important;
            line-height: 1.3 !important;
          }
          .merge-panel .text-muted,
          .merge-panel-label,
          .merge-result-preview .text-muted.text-uppercase,
          .merge-result-preview-col .text-uppercase {
            font-size: var(--merge-label-size) !important;
          }
          .merge-panel .fw-semibold,
          .merge-panel .fw-bold:not(h5),
          .merge-result-preview-value,
          .merge-result-preview-body .fw-semibold {
            font-size: var(--merge-value-size) !important;
          }
          .merge-panel .badge,
          .merge-result-preview .badge {
            font-size: var(--merge-badge-size) !important;
          }
          .merge-panel .badge.merge-receipt-badge,
          .merge-result-preview .badge.merge-receipt-badge,
          .merge-receipt-badge,
          .badge.merge-receipt-badge {
            font-size: 13px !important;
            padding: 4px 8px !important;
            font-weight: 600 !important;
            line-height: 1.25 !important;
          }
          .merge-result-preview-header .text-muted:not(.text-uppercase) {
            font-size: var(--merge-meta-size) !important;
          }
          @media (max-width: 1199.98px) {
            .merge-panel,
            .merge-result-preview {
              --merge-title-size: clamp(0.78rem, 0.7rem + 0.4vw, 0.9rem);
              --merge-label-size: clamp(0.62rem, 0.56rem + 0.3vw, 0.72rem);
              --merge-value-size: clamp(0.7rem, 0.64rem + 0.35vw, 0.84rem);
            }
          }
          @media (max-width: 991.98px) {
            .merge-panel,
            .merge-result-preview {
              --merge-title-size: 0.9rem;
              --merge-label-size: 0.7rem;
              --merge-value-size: 0.84rem;
              --merge-meta-size: 0.7rem;
              --merge-badge-size: 0.68rem;
            }
            .merge-result-preview-body {
              gap: 0.35rem !important;
            }
          }
          @media (max-width: 575.98px) {
            .merge-panel,
            .merge-result-preview {
              --merge-title-size: 0.85rem;
              --merge-label-size: 0.68rem;
              --merge-value-size: 0.8rem;
              --merge-meta-size: 0.68rem;
              --merge-badge-size: 0.65rem;
            }
          }
          /* Selected Into that is also a best match — darker green, not blue */
          html:not([data-bs-theme='dark']) .merge-v2-to-row.merge-v2-best-match-row {
            background-color: #9ad9b8 !important;
            outline: 2px solid #1aae70;
            outline-offset: -2px;
          }
          /* Light: paint cells so the row is one even color (no column separators) */
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-from-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-from-row:hover > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-from-row:nth-child(even) > td {
            background-color: #fff5e8 !important;
            box-shadow: none !important;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            --bs-table-bg: #fff5e8 !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-state: transparent !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-best-match-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-best-match-row:hover > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-best-match-row:nth-child(even) > td {
            background-color: #d1f0e1 !important;
            box-shadow: none !important;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            --bs-table-bg: #d1f0e1 !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-state: transparent !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row:hover > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row:nth-child(even) > td {
            background-color: #e8f4ff !important;
            box-shadow: none !important;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            --bs-table-bg: #e8f4ff !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-state: transparent !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row.merge-v2-best-match-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row.merge-v2-best-match-row:hover > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-to-row.merge-v2-best-match-row:nth-child(even) > td {
            background-color: #9ad9b8 !important;
            box-shadow: none !important;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            --bs-table-bg: #9ad9b8 !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-state: transparent !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-match-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-match-row:hover > td,
          html:not([data-bs-theme='dark']) .merge-v2-table.table-row-dashed tbody tr.merge-v2-match-row:nth-child(even) > td {
            background-color: #e8fff3 !important;
            box-shadow: none !important;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            --bs-table-bg: #e8fff3 !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-state: transparent !important;
          }
          .merge-v2-swap-btn {
            position: relative;
            z-index: 3;
            width: 36px;
            height: 36px;
            min-width: 36px;
            min-height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            pointer-events: auto;
          }
          .merge-v2-table thead th {
            position: sticky;
            top: 0;
            z-index: 5;
            background: #f5f8fa !important;
            box-shadow: 0 1px 0 #e4e6ef;
            color: #3f4254 !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.02em;
          }
          .merge-v2-table tbody td .merge-v2-date {
            color: #3f4254 !important;
            font-weight: 600;
          }
          /* Light only: dark ink on pastel From/To/match rows */
          html:not([data-bs-theme='dark']) .merge-v2-from-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-to-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-best-match-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-match-row > td,
          html:not([data-bs-theme='dark']) .merge-v2-from-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html:not([data-bs-theme='dark']) .merge-v2-to-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html:not([data-bs-theme='dark']) .merge-v2-best-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html:not([data-bs-theme='dark']) .merge-v2-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *) {
            color: #181c32 !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-from-row .badge.badge-warning,
          html:not([data-bs-theme='dark']) .merge-v2-to-row .badge.badge-primary,
          html:not([data-bs-theme='dark']) .merge-v2-from-row .badge.badge-success,
          html:not([data-bs-theme='dark']) .merge-v2-to-row .badge.badge-success {
            color: #ffffff !important;
          }
          html:not([data-bs-theme='dark']) .merge-v2-from-row .badge.badge-light-success,
          html:not([data-bs-theme='dark']) .merge-v2-to-row .badge.badge-light-success,
          html:not([data-bs-theme='dark']) .merge-v2-best-match-row .badge.badge-light-success {
            color: #0a7a45 !important;
          }
          .merge-v2-compare-match td.match-cell {
            background-color: #d1f0e1 !important;
          }
          .merge-v2-match-filters .form-check-label {
            color: #3f4254 !important;
            font-weight: 500;
          }
          .merge-v2-match-filters .form-check-input:checked + .form-check-label {
            color: #181c32 !important;
            font-weight: 700 !important;
          }

          /* Dark mode — Organizations-style: deep fills + light text (beat Bootstrap table inset wash) */
          html[data-bs-theme='dark'] .merge-v2-from-row,
          [data-bs-theme='dark'] .merge-v2-from-row,
          .merge-theme-dark .merge-v2-from-row { background-color: #3a3420 !important; }
          html[data-bs-theme='dark'] .merge-v2-to-row,
          [data-bs-theme='dark'] .merge-v2-to-row,
          .merge-theme-dark .merge-v2-to-row { background-color: #1e2a3d !important; }
          html[data-bs-theme='dark'] .merge-v2-best-match-row,
          [data-bs-theme='dark'] .merge-v2-best-match-row,
          .merge-theme-dark .merge-v2-best-match-row { background-color: #1a3329 !important; }
          html[data-bs-theme='dark'] .merge-v2-match-row,
          [data-bs-theme='dark'] .merge-v2-match-row,
          .merge-theme-dark .merge-v2-match-row { background-color: #1f3a2e !important; }
          html[data-bs-theme='dark'] .merge-v2-from-row > td,
          html[data-bs-theme='dark'] .merge-v2-to-row > td,
          html[data-bs-theme='dark'] .merge-v2-best-match-row > td,
          html[data-bs-theme='dark'] .merge-v2-match-row > td,
          .merge-theme-dark .merge-v2-from-row > td,
          .merge-theme-dark .merge-v2-to-row > td,
          .merge-theme-dark .merge-v2-best-match-row > td,
          .merge-theme-dark .merge-v2-match-row > td {
            color: #e4e6ef !important;
            box-shadow: none !important;
            --bs-table-bg: transparent !important;
            --bs-table-accent-bg: transparent !important;
            --bs-table-bg-type: transparent !important;
            --bs-table-bg-state: transparent !important;
            --bs-table-color: #e4e6ef !important;
            --bs-table-color-state: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-v2-from-row > td,
          .merge-theme-dark .merge-v2-from-row > td { background-color: #3a3420 !important; }
          html[data-bs-theme='dark'] .merge-v2-to-row > td,
          .merge-theme-dark .merge-v2-to-row > td { background-color: #1e2a3d !important; }
          html[data-bs-theme='dark'] .merge-v2-best-match-row > td,
          .merge-theme-dark .merge-v2-best-match-row > td { background-color: #1a3329 !important; }
          html[data-bs-theme='dark'] .merge-v2-to-row.merge-v2-best-match-row > td,
          .merge-theme-dark .merge-v2-to-row.merge-v2-best-match-row > td { background-color: #1e2a3d !important; }
          html[data-bs-theme='dark'] .merge-v2-match-row > td,
          .merge-theme-dark .merge-v2-match-row > td { background-color: #1f3a2e !important; }
          html[data-bs-theme='dark'] .merge-v2-from-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html[data-bs-theme='dark'] .merge-v2-to-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html[data-bs-theme='dark'] .merge-v2-best-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          html[data-bs-theme='dark'] .merge-v2-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          .merge-theme-dark .merge-v2-from-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          .merge-theme-dark .merge-v2-to-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          .merge-theme-dark .merge-v2-best-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *),
          .merge-theme-dark .merge-v2-match-row > td *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *) {
            color: #e4e6ef !important;
          }
          /* All listing dates (including non-highlighted rows) */
          html[data-bs-theme='dark'] .merge-v2-table tbody td,
          html[data-bs-theme='dark'] .merge-v2-table tbody td .merge-v2-date,
          [data-bs-theme='dark'] .merge-v2-table tbody td .merge-v2-date,
          .merge-theme-dark .merge-v2-table tbody td,
          .merge-theme-dark .merge-v2-table tbody td .merge-v2-date {
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-v2-table thead th,
          [data-bs-theme='dark'] .merge-v2-table thead th,
          .merge-theme-dark .merge-v2-table thead th {
            background: #2b2b40 !important;
            box-shadow: 0 1px 0 #323248;
            color: #a1a5b7 !important;
          }
          html[data-bs-theme='dark'] .merge-v2-compare-match td.match-cell,
          [data-bs-theme='dark'] .merge-v2-compare-match td.match-cell,
          .merge-theme-dark .merge-v2-compare-match td.match-cell {
            background-color: #1f3a2e !important;
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-v2-match-filters .form-check-label,
          [data-bs-theme='dark'] .merge-v2-match-filters .form-check-label,
          .merge-theme-dark .merge-v2-match-filters .form-check-label {
            color: #a1a5b7 !important;
          }
          html[data-bs-theme='dark'] .merge-v2-match-filters .form-check-input:checked + .form-check-label,
          [data-bs-theme='dark'] .merge-v2-match-filters .form-check-input:checked + .form-check-label,
          .merge-theme-dark .merge-v2-match-filters .form-check-input:checked + .form-check-label {
            color: #ffffff !important;
          }
          html[data-bs-theme='dark'] .merge-v2-from-row .badge.badge-warning,
          html[data-bs-theme='dark'] .merge-v2-to-row .badge.badge-primary,
          .merge-theme-dark .merge-v2-from-row .badge.badge-warning,
          .merge-theme-dark .merge-v2-to-row .badge.badge-primary {
            color: #ffffff !important;
          }
          /* Panel fills (From / Into / Preview) — deep dark, not cream */
          html[data-bs-theme='dark'] .merge-panel--from,
          .merge-theme-dark .merge-panel--from { background-color: #3a3420 !important; border-color: #c9a227 !important; }
          html[data-bs-theme='dark'] .merge-panel--into,
          .merge-theme-dark .merge-panel--into { background-color: #1e2a3d !important; border-color: #3699ff !important; }
          html[data-bs-theme='dark'] .merge-panel--preview,
          .merge-theme-dark .merge-panel--preview { background-color: #1a3329 !important; border-color: #50cd89 !important; }
          html[data-bs-theme='dark'] .merge-panel-card,
          html[data-bs-theme='dark'] .merge-panel-receipt,
          .merge-theme-dark .merge-panel-card,
          .merge-theme-dark .merge-panel-receipt {
            background-color: #1e1e2d !important;
            border-color: #323248 !important;
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-result-preview,
          .merge-theme-dark .merge-result-preview {
            background-color: #1a3329 !important;
            border-color: #50cd89 !important;
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-result-preview-header,
          .merge-theme-dark .merge-result-preview-header {
            background-color: #234636 !important;
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-result-preview-body,
          .merge-theme-dark .merge-result-preview-body {
            background-color: #1a3329 !important;
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-panel--from *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          html[data-bs-theme='dark'] .merge-panel--into *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          html[data-bs-theme='dark'] .merge-panel--preview *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          html[data-bs-theme='dark'] .merge-result-preview *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          .merge-theme-dark .merge-panel--from *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          .merge-theme-dark .merge-panel--into *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          .merge-theme-dark .merge-panel--preview *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img),
          .merge-theme-dark .merge-result-preview *:not(.badge):not(.badge *):not(.btn):not(.btn *):not(svg):not(svg *):not(img) {
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-panel .text-muted,
          html[data-bs-theme='dark'] .merge-result-preview .text-muted,
          html[data-bs-theme='dark'] .merge-panel-label,
          .merge-theme-dark .merge-panel .text-muted,
          .merge-theme-dark .merge-result-preview .text-muted,
          .merge-theme-dark .merge-panel-label {
            color: #a1a5b7 !important;
          }
          /* Dark mode: ensure all icons render white */
          html[data-bs-theme='dark'] .merge-line-items-v2-content .ss-source-icon,
          .merge-theme-dark .ss-source-icon {
            filter: brightness(0) invert(1) !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .ss-source-icon svg path,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .ss-source-icon svg [fill]:not([fill='none']),
          .merge-theme-dark .ss-source-icon svg path,
          .merge-theme-dark .ss-source-icon svg [fill]:not([fill='none']) {
            fill: #ffffff !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-header .btn-icon,
          .merge-theme-dark .modal-header .btn-icon,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-header .btn-icon .svg-icon,
          .merge-theme-dark .modal-header .btn-icon .svg-icon {
            color: #ffffff !important;
            filter: brightness(0) invert(1) !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-header .btn-icon svg path,
          .merge-theme-dark .modal-header .btn-icon svg path {
            fill: #ffffff !important;
          }
          html[data-bs-theme='dark'] .merge-preview-icon,
          .merge-theme-dark .merge-preview-icon,
          html[data-bs-theme='dark'] .merge-preview-icon svg,
          .merge-theme-dark .merge-preview-icon svg,
          html[data-bs-theme='dark'] .merge-result-preview-col svg,
          .merge-theme-dark .merge-result-preview-col svg {
            color: #ffffff !important;
            fill: #ffffff !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .btn-light svg,
          .merge-theme-dark .btn-light svg,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .btn-light-primary svg,
          .merge-theme-dark .btn-light-primary svg {
            color: #ffffff !important;
            fill: #ffffff !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .svg-icon-gray-500,
          .merge-theme-dark .svg-icon-gray-500,
          html[data-bs-theme='dark'] .merge-line-items-v2-content .svg-icon-gray-500 svg,
          .merge-theme-dark .svg-icon-gray-500 svg {
            color: #ffffff !important;
            filter: brightness(0) invert(1) !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-footer .text-gray-700,
          .merge-theme-dark .modal-footer .text-gray-700 {
            color: #e4e6ef !important;
          }
          html[data-bs-theme='dark'] .merge-line-items-v2-content .modal-footer .text-muted svg,
          .merge-theme-dark .modal-footer .text-muted svg {
            color: #ffffff !important;
            fill: #ffffff !important;
          }
        `}</style>
        <div className="modal-header">
          <h2>Merge Line Items (V2)</h2>
          <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={handleModalClose}>
            <KTSVG className="svg-icon-1" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>

        <div className="modal-body px-lg-10" ref={modalBodyRef}>
          {selectedTargetId == null ? (
            <Alert variant="primary" className="py-2 mb-2">
              <p className="mb-0 fs-7">
                All Sources lists Bank and Web/Mobile candidates together. Case 1 merges into Bank; Case 2 merges into
                another SpendSail (Web/Mobile) line item. Best matches (same amount and/or date) are highlighted and
                selected automatically when available. Use filters to narrow the list, or All Items (Not Submitted) to
                browse every draft/clarification candidate. Swap From/Into only when both items are non-Bank; Bank is
                always the kept (Into) line item.
              </p>
            </Alert>
          ) : null}

          <div className="d-flex flex-wrap align-items-center gap-2 gap-lg-3 mb-2">
            <div className="btn-group">
              <button
                type="button"
                className={`btn btn-sm ${activeTab === 'all' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => switchTab('all')}
              >
                All Sources
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === 'bank' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => switchTab('bank')}
              >
                Item Source (Case 1 - Bank)
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === 'ss' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => switchTab('ss')}
              >
                Item Source (Case 2 - Web / Mobile)
              </button>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3 ms-lg-2 merge-v2-match-filters">
              <Form.Check
                type="radio"
                name="mergeMatchFilterV2"
                id="match-amount-only-v2"
                label="Same amount only"
                checked={matchFilter === 'amount'}
                onChange={() => {
                  setMatchFilter('amount')
                  clearTargetSelection()
                }}
              />
              <Form.Check
                type="radio"
                name="mergeMatchFilterV2"
                id="match-date-only-v2"
                label="Same item date only"
                checked={matchFilter === 'date'}
                onChange={() => {
                  setMatchFilter('date')
                  clearTargetSelection()
                }}
              />
              <Form.Check
                type="radio"
                name="mergeMatchFilterV2"
                id="match-date-amount-v2"
                label="Same item date and amount only"
                checked={matchFilter === 'dateAndAmount'}
                onChange={() => {
                  setMatchFilter('dateAndAmount')
                  clearTargetSelection()
                }}
              />
              <Form.Check
                type="radio"
                name="mergeMatchFilterV2"
                id="match-all-items-v2"
                label="All Items (Not Submitted)"
                checked={matchFilter === 'all'}
                onChange={() => {
                  setMatchFilter('all')
                  clearTargetSelection()
                }}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
            <span className="fs-7 fw-semibold merge-count-label">
              Showing {filteredListRows.length}
              {listSearch.trim() ? ` of ${listRows.length}` : ''}{' '}
              {filteredListRows.length === 1 ? 'entry' : 'entries'}
            </span>
            <div className="position-relative" style={{ minWidth: 220, maxWidth: 320, flex: '1 1 220px' }}>
              <KTSVG
                path="/media/icons/duotune/general/gen021.svg"
                className="svg-icon-2 svg-icon-gray-500 position-absolute top-50 translate-middle-y ms-3"
              />
              <input
                type="search"
                className="form-control form-control-sm form-control-solid ps-10"
                placeholder="Search from list..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                aria-label="Search merge candidates"
              />
            </div>
          </div>

          <form className="form w-100" onSubmit={handleSubmit} id="mergeLineItemFormV2">
            <Tooltip id="merge-v2-open-compare" place="left" />
            <Tooltip id="merge-v2-open-receipt" place="left" />
            <div className="table-responsive table-height">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 merge-v2-table">
                <thead>
                  <tr>
                    {hiddenColumns.includes('select') && <th></th>}
                    {showOrderColumn && hiddenColumns.includes('order') && <th>Order</th>}
                    {activeTab === 'all' && <th>Item Source</th>}
                    {/* {hiddenColumns.includes('trackingId') && <th>Tracking ID / Status / Report Category</th>} */}
                    {hiddenColumns.includes('trackingId') && <th>Report Details</th>}
                    {/* {showAdminMeta && hiddenColumns.includes('reportCategory') && <th>Report Category</th>} */}
                    {showAdminMeta && hiddenColumns.includes('title') && <th>Title</th>}
                    {/* {showAdminMeta && hiddenColumns.includes('reportStatus') && <th>Report Status</th>} */}
                    {showInvoiceDataDraftId && hiddenColumns.includes('invoiceDataDraftId') && (
                      <th>SS ReceiptID</th>
                    )}
                    {hiddenColumns.includes('invoiceNo') && <th>Receipt No</th>}
                    {hiddenColumns.includes('itemDate') && <th>{labelKey.lineItemDate}</th>}
                    {hiddenColumns.includes('category') && <th>{labelKey.category}</th>}
                    {hiddenColumns.includes('amount') && <th>{labelKey.amount}</th>}
                    {hiddenColumns.includes('vendor') && <th>{labelKey.vendor}</th>}
                    {hiddenColumns.includes('paymentMethod') && <th>{labelKey.paymentMethod}</th>}
                    {hiddenColumns.includes('attachment') && <th className="min-w-175px">Attachment</th>}
                    {hiddenColumns.includes('description') && <th>{labelKey.description}</th>}
                    {hiddenColumns.includes('source') && <th>Item Source & Created Date</th>}
                    {/* {hiddenColumns.includes('createdDate') && <th>Item Created Date</th>} */}
                    <th className="text-end pe-2 border-0">
                      {hiddenColumns.includes('columnsMenu') && (
                        <TableSettingMenu>
                          <TextField
                            rightLabel="Select"
                            type="checkbox"
                            onChange={() => handleToggleColumn('select')}
                            checked={hiddenColumns.includes('select')}
                            fieldClass="mb-4"
                          />
                          {activeTab === 'ss' && (
                            <TextField
                              rightLabel="Order"
                              type="checkbox"
                              onChange={() => handleToggleColumn('order')}
                              checked={hiddenColumns.includes('order')}
                              fieldClass="mb-4"
                            />
                          )}
                          <TextField
                            rightLabel="Tracking ID"
                            type="checkbox"
                            onChange={() => handleToggleColumn('trackingId')}
                            checked={hiddenColumns.includes('trackingId')}
                            fieldClass="mb-4"
                          />
                          {/* <TextField
                              rightLabel="Report Category"
                              type="checkbox"
                              onChange={() => handleToggleColumn('reportCategory')}
                              checked={hiddenColumns.includes('reportCategory')}
                              fieldClass="mb-4"
                          /> */}
                          {showAdminMeta && (
                            <>
                              <TextField
                                rightLabel="Title"
                                type="checkbox"
                                onChange={() => handleToggleColumn('title')}
                                checked={hiddenColumns.includes('title')}
                                fieldClass="mb-4"
                              />
                              {/* <TextField
                                rightLabel="Report Status"
                                type="checkbox"
                                onChange={() => handleToggleColumn('reportStatus')}
                                checked={hiddenColumns.includes('reportStatus')}
                                fieldClass="mb-4"
                              /> */}
                              {showInvoiceDataDraftId ? (
                                <TextField
                                  rightLabel="SS ReceiptID"
                                  type="checkbox"
                                  onChange={() => handleToggleColumn('invoiceDataDraftId')}
                                  checked={hiddenColumns.includes('invoiceDataDraftId')}
                                  fieldClass="mb-4"
                                />
                              ) : null}
                              <TextField
                                rightLabel="Receipt No"
                                type="checkbox"
                                onChange={() => handleToggleColumn('invoiceNo')}
                                checked={hiddenColumns.includes('invoiceNo')}
                                fieldClass="mb-4"
                              />
                            </>
                          )}
                          <TextField
                            rightLabel={labelKey.lineItemDate || 'Line Item Date'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('itemDate')}
                            checked={hiddenColumns.includes('itemDate')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel={labelKey.category || 'Category'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('category')}
                            checked={hiddenColumns.includes('category')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel={labelKey.amount || 'Amount'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('amount')}
                            checked={hiddenColumns.includes('amount')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel={labelKey.vendor || 'Vendor'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('vendor')}
                            checked={hiddenColumns.includes('vendor')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel={labelKey.paymentMethod || 'Payment Method'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('paymentMethod')}
                            checked={hiddenColumns.includes('paymentMethod')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel="Attachment"
                            type="checkbox"
                            onChange={() => handleToggleColumn('attachment')}
                            checked={hiddenColumns.includes('attachment')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel={labelKey.description || 'Description'}
                            type="checkbox"
                            onChange={() => handleToggleColumn('description')}
                            checked={hiddenColumns.includes('description')}
                            fieldClass="mb-4"
                          />
                          <TextField
                            rightLabel="Item Source"
                            type="checkbox"
                            onChange={() => handleToggleColumn('source')}
                            checked={hiddenColumns.includes('source')}
                            fieldClass="mb-4"
                          />
                          {/* <TextField
                            rightLabel="Item Created Date"
                            type="checkbox"
                            onChange={() => handleToggleColumn('createdDate')}
                            checked={hiddenColumns.includes('createdDate')}
                            fieldClass="mb-4"
                          /> */}
                        </TableSettingMenu>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredListRows.length > 0 ? (
                    filteredListRows.map((item: MergeCandidate) => {
                      // Opening / Prefer item always matches itself — never show Date/Amount match badges on it
                      const isPreferSource =
                        Number(item?.expenseItemId) > 0 &&
                        Number(item.expenseItemId) === Number(excludeExpenseItemId)
                      const matchScore = isPreferSource
                        ? 0
                        : getMatchScore(item, preferAmount, preferItemDate)
                      const isBest = !isPreferSource && bestMatchScore > 0 && matchScore === bestMatchScore
                      const { amountMatch, dateMatch } = isPreferSource
                        ? { amountMatch: false, dateMatch: false }
                        : getFieldMatchFlags(item, preferAmount, preferItemDate)
                      const meta = getMergeAdminMeta(item)
                      const listTrackingId = item?.trackingId || item?.expense?.trackingId || ''
                      const listExpenseId = Number(item?.expenseId ?? item?.expense?.expenseId ?? 0)
                      const listExpenseStatusId = Number(
                        item?.expenseStatusId ??
                        item?.expenseStatus?.id ??
                        item?.expense?.expenseStatus?.id ??
                        item?.expense?.expenseStatusId ??
                        0
                      )
                      const listExpenseStatusTitle =
                        item?.expenseStatusTitle ||
                        item?.expenseStatus?.title ||
                        item?.expense?.expenseStatus?.title ||
                        ''
                      // Show real report status under Tracking ID (never "Not Submitted" / empty placeholders)
                      const showExpenseStatusBadge =
                        listExpenseStatusTitle !== 'Not Submitted' &&
                        ((listExpenseStatusId > 0 && !!listExpenseStatusTitle) ||
                          (listExpenseId > 0 && !!listExpenseStatusTitle) ||
                          (listExpenseStatusId > 0 && !listExpenseStatusTitle))
                      const expenseStatusBadgeTitle =
                        listExpenseStatusTitle || (listExpenseStatusId > 0 ? 'Status' : '')
                      const mergeRole = getListMergeRole(item.expenseItemId)
                      const isOpeningRow = !!item._isOpeningItem
                      const rowClass = [
                        mergeRole === 'from' ? 'merge-v2-from-row' : '',
                        mergeRole === 'to' ? 'merge-v2-to-row' : '',
                        isBest && mergeRole !== 'from' ? 'merge-v2-best-match-row' : '',
                        !isBest && matchScore > 0 && !mergeRole ? 'merge-v2-match-row' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')
                      const candidateId = Number(item?.expenseItemId ?? item?.id ?? 0)
                      return (
                        <tr
                          key={`${item._mergeSource || 'x'}-${candidateId || 'row'}${isOpeningRow ? '-opening' : ''}`}
                          className={`${rowClass || ''} ${isOpeningRow ? '' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (isOpeningRow || candidateId <= 0) return
                            openComparePanel(candidateId, item)
                          }}
                        >
                          {hiddenColumns.includes('select') && (
                            <td className="ps-4" onClick={(e) => e.stopPropagation()}>
                              {isOpeningRow ? (
                                <span className="text-muted fs-8"></span>
                              ) : (
                                <div className="form-check form-check-sm form-check-custom form-check-solid me-3">
                                  <input
                                    type="radio"
                                    name="mergeTargetV2"
                                    className="form-check-input"
                                    checked={Boolean(
                                      selectedTargetId != null &&
                                      candidateId > 0 &&
                                      Number(selectedTargetId) === candidateId
                                    )}
                                    onChange={() => handleRadioChange(candidateId, item)}
                                  />
                                </div>
                              )}
                              {mergeRole === 'from' ? (
                                <span className="badge badge-warning mt-1 d-inline-block">Merge From</span>
                              ) : null}
                              {mergeRole === 'to' ? (
                                <span className="badge badge-primary mt-1 d-inline-block">Merge To</span>
                              ) : null}
                            </td>
                          )}
                          {showOrderColumn && hiddenColumns.includes('order') && (
                            <td onClick={(e) => e.stopPropagation()}>
                              {isOpeningRow ? (
                                ''
                              ) : (
                                <div className="d-flex gap-1">
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-light"
                                    title="Move up"
                                    onClick={() => moveSsRow(item.expenseItemId, 'up')}
                                  >
                                    <FaArrowUp />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-light"
                                    title="Move down"
                                    onClick={() => moveSsRow(item.expenseItemId, 'down')}
                                  >
                                    <FaArrowDown />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                          {activeTab === 'all' && (
                            <td>
                              <span
                                className={`badge ${item._mergeSource === 'bank' ? 'badge-light-info' : 'badge-light-primary'
                                  }`}
                              >
                                {item._mergeSource === 'bank' ? 'Bank' : 'Web / Mobile'}
                              </span>
                            </td>
                          )}
                          {hiddenColumns.includes('trackingId') && (
                            <td>
                              {listTrackingId || showExpenseStatusBadge ? (
                                <div className="d-flex flex-column gap-1 align-items-start">
                                  {listTrackingId ? <span>{listTrackingId}</span> : null}
                                  {showExpenseStatusBadge ? (
                                    <ExpenseStatusComp
                                      expenseStatusId={listExpenseStatusId}
                                      expenseStatusTitle={expenseStatusBadgeTitle}
                                    />
                                  ) 
                                  : null}
                                  {meta.reportCategory ? <span>{meta.reportCategory}</span> : null}
                                </div>
                              ) : null}
                            </td>
                          )}
                          {showAdminMeta && hiddenColumns.includes('title') && (
                            <td className="text-wrap" style={{ maxWidth: 160 }}>
                              {meta.title}
                            </td>
                          )}
                          {/* {showAdminMeta && hiddenColumns.includes('reportStatus') && (
                            <td>
                              {showExpenseStatusBadge ? (
                                <ExpenseStatusComp
                                  expenseStatusId={listExpenseStatusId}
                                  expenseStatusTitle={expenseStatusBadgeTitle}
                                />
                              ) : null}
                            </td>
                          )} */}
                          {showInvoiceDataDraftId && hiddenColumns.includes('invoiceDataDraftId') && (
                            <td className="text-center">{meta.invoiceDataDraftId}</td>
                          )}
                          {hiddenColumns.includes('invoiceNo') && (
                            <td className="text-center">{item?.invoiceNo}</td>
                          )}
                          {hiddenColumns.includes('itemDate') && (
                            <td className="text-center">
                              <div className="d-inline-flex flex-wrap align-items-center gap-1">
                                <span className="merge-v2-date">
                                  <DateComp formattedDate={item?.itemDate} />
                                </span>
                                {dateMatch ? (
                                  <span
                                    className={`badge ${isBest && !amountMatch ? 'badge-success' : 'badge-light-success'
                                      }`}
                                  >
                                    {isBest && !amountMatch ? 'Best match' : 'Date match'}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          )}
                          {hiddenColumns.includes('category') && <td>{item?.expenseCategory?.title}</td>}
                          {hiddenColumns.includes('amount') && (
                            <td
                              className={
                                item?.debitOrCreditIndicator ===
                                  constraintConfig.debitOrCreditIndicator.creditIndicator ? 'text-danger pe-4' : 'pe-4'
                              }
                            >
                              <div className="d-inline-flex flex-wrap align-items-center gap-1">
                                <CurrencyComp amount={item?.amount} />
                                {amountMatch ? (
                                  <span className={`badge ${isBest ? 'badge-success' : 'badge-light-success'}`}>
                                    {isBest ? 'Best match' : 'Amount match'}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          )}
                          {hiddenColumns.includes('vendor') && <td>{item?.vendor?.name}</td>}
                          {hiddenColumns.includes('paymentMethod') && (
                            <td>{item?.paymentMethod?.title}</td>
                          )}
                          {hiddenColumns.includes('attachment') && (() => {
                            const rawReceiptNo = item?.invoiceNo || item?.receiptInvoiceNo
                            const receiptNoDisplay = rawReceiptNo
                              ? formatReceiptNumberDisplay(String(rawReceiptNo))
                              : item?.invoiceDataDraftId
                              ? `Draft #${item.invoiceDataDraftId}`
                              : getReceiptNumber(item) !== '—'
                              ? formatReceiptNumberDisplay(getReceiptNumber(item))
                              : '—'
                            const uploadedDt =
                              item?.receiptUploadedDateTime ||
                              item?.receiptUploadedDate ||
                              item?.invoiceDataDraft?.createdDT
                            const hasAttachment = Boolean(getMergeAttachmentUrl(item))

                            return (
                              <td onClick={(e) => e.stopPropagation()} className="align-middle min-w-175px">
                                <div className="d-flex align-items-center gap-2">
                                  {hasAttachment ? (
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-light flex-shrink-0"
                                      data-tooltip-id="merge-v2-open-receipt"
                                      data-tooltip-content={`Open receipt # ${getReceiptNumber(item)}`}
                                      onClick={() => openPdfInBrowserOrLightbox(getMergeAttachmentUrl(item))}
                                      style={{ width: 32, height: 32 }}
                                    >
                                      <FaEye />
                                    </button>
                                  ) : (
                                    <span
                                      className="btn btn-icon btn-sm btn-light disabled flex-shrink-0 opacity-25"
                                      style={{ width: 32, height: 32, cursor: 'default' }}
                                      title="No attachment available"
                                    >
                                      <FaEye />
                                    </span>
                                  )}
                                  <div className="d-flex flex-column min-w-0" style={{ lineHeight: 1.25 }}>
                                    <span
                                      className="fw-bold text-truncate"
                                      style={{ fontSize: 12 }}
                                      title={String(rawReceiptNo || receiptNoDisplay)}
                                    >
                                      {receiptNoDisplay}
                                    </span>
                                    {uploadedDt ? (
                                      <span
                                        className="merge-v2-date text-muted"
                                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                      >
                                        <DateTimeComp formattedDate={uploadedDt} />
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            )
                          })()}
                          {hiddenColumns.includes('description') && (
                            <td className="text-wrap">
                              {item?.itemDetail && (
                                <ReactReadMoreReadLess
                                  charLimit={30}
                                  readMoreText="Read more"
                                  readLessText="Read less"
                                  readMoreClassName="readMore"
                                  readLessClassName="readLess"
                                >
                                  {item.itemDetail}
                                </ReactReadMoreReadLess>
                              )}
                            </td>
                          )}
                          {hiddenColumns.includes('source') && (
                            <td className="text-center">{renderSourceIcon(item?.recordSourceId)}
                              {item?.createdDate ? (
                                <span className="merge-v2-date"><br></br>
                                  <DateTimeComp formattedDate={item.createdDate} />
                                </span>
                              ) : (
                                ''
                              )}
                            </td>
                          )}
                          {/* {hiddenColumns.includes('createdDate') && (
                            <td className="text-center">
                              {item?.createdDate ? (
                                <span className="merge-v2-date">
                                  <DateTimeComp formattedDate={item.createdDate} />
                                </span>
                              ) : (
                                ''
                              )}
                            </td>
                          )} */}
                          <td />
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={20} className="text-center text-muted py-3">
                        {isLoading ? 'Loading...' : 'No matching candidates'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </form>

          {fromItem ? (
            <div className="px-1 pb-1" id="merge-v2-target-panel">
              <div className="d-flex flex-nowrap align-items-stretch" style={{ gap: 1 }}>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <MergeSelectedLineItemHeader
                    key={`from-${fromItem?.expenseItemId}`}
                    selectedDraftData={fromItem}
                    title="Merge From"
                    variant="from"
                    matchHints={selectedMatchHints}
                  />
                </div>
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ flex: '0 0 auto', width: 48, padding: '0 2px', zIndex: 3 }}
                >
                  {intoItem ? (
                    <>
                      <button
                        type="button"
                        className={`btn btn-sm btn-icon merge-v2-swap-btn ${canSwapMergePair ? 'btn-primary' : 'btn-light'}`}
                        disabled={!canSwapMergePair}
                        title={
                          canSwapMergePair
                            ? 'Swap Merge From and Into (only when neither is Bank)'
                            : 'Bank is always kept (Into) — swap is only available when neither line item is Bank'
                        }
                        onClick={handleSwapMergePair}
                      >
                        <FaExchangeAlt />
                      </button>
                      <span className="fs-9 text-muted text-center">Swap</span>
                      {!canSwapMergePair ? (
                        <span className="text-muted fs-9 text-center">Bank always Into</span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-muted fs-9 text-center">Select Merge To</span>
                  )}
                </div>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  {intoItem ? (
                    <MergeSelectedLineItemHeader
                      key={`into-${intoItem?.expenseItemId}`}
                      selectedDraftData={intoItem}
                      title={mergeMode === 'bank' ? 'Merge To (Bank — keep)' : 'Merge To (keep)'}
                      variant="into"
                      onClose={closeComparePanel}
                      matchHints={selectedMatchHints}
                    />
                  ) : (
                    <div className="border rounded bg-light px-3 py-5 h-100 d-flex align-items-center justify-content-center text-muted">
                      Merge To — select a candidate above
                    </div>
                  )}
                </div>
              </div>
              {intoItem ? (
                <MergeResultPreviewRow
                  fromItem={fromItem}
                  intoItem={intoItem}
                  mergeMode={mergeMode}
                  matchHints={selectedMatchHints}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          className="modal-footer merge-modal-footer-bar d-flex flex-wrap align-items-center justify-content-between gap-2 px-lg-10"
        >
          <div className="d-flex flex-wrap align-items-center gap-2 gap-lg-3" style={{ fontSize: 12 }}>
            <span className="d-inline-flex align-items-center gap-1 text-gray-700">
              <FaTimesCircle className="text-danger" size={13} />
              Merge From (Soft Delete)
            </span>
            <span className="d-inline-flex align-items-center gap-1 text-gray-700">
              <FaShieldAlt className="text-success" size={13} />
              Bank is always kept (into)
            </span>
            <span className="d-inline-flex align-items-center gap-1 text-gray-700">
              <FaExchangeAlt className={isMergeDark ? 'text-white' : 'text-muted'} size={13} />
              Swap Merge From and Into (only when neither is Bank)
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-light" onClick={handleModalClose}>
              {labelKey.cancel}
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              disabled={isLoading || selectedTargetId == null}
              form="mergeLineItemFormV2"
            >
              Compare & Merge
            </button>
          </div>
        </div>
      </Modal>

      <MergeLineItemConfirmationModalV2
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        onConfirm={handleModalClose}
        mergeMode={mergeMode}
        selectedDraftData={fromItem}
        selectedTargetData={intoItem}
        canSwap={canSwapMergePair}
        onSwap={handleSwapMergePair}
        refetch={refetch}
        onSuccess={onSuccess}
      />

      {lightboxOpen && lightboxUrl ? (
        <Lightbox
          mainSrc={lightboxUrl}
          onCloseRequest={() => setLightboxOpen(false)}
          imageCaption="Attachment"
        />
      ) : null}
    </>
  )
}

export default BetterLineItemMergeModal
